/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect } from "react";
import { useState, useRef } from "react";
import styles from "./styles.module.css";
import { createContext } from "react";
import MimeTypes from "../../constants/MimeTypes";

export const MFRUploadDialogContext = createContext({});
const loadingIcon = require("../../assets/esign-ui-icons/saving-ico-white.svg");

const MFRUploadDialog = ({ children }) => {
  const fileUploadRef = useRef("");
  const [fileName, setFilename] = useState(null);
  const [base64, setBase64] = useState(null);
  const [file, setFile] = useState(null);
  const [onSave, setOnSave] = useState([]);
  const [onCancel, setOnCancel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldShowFileTypeError, setShouldShowFileTypeError] = useState(false);
  const [shouldDisableUploadBtn, setShouldDisableUploadBtn] = useState(true);
  const [isShow, setIsShow] = useState(false);

  useEffect(() => {
    setShouldDisableUploadBtn(loading || shouldShowFileTypeError || !file);
  }, [loading, shouldShowFileTypeError, file]);

  useEffect(() => {
    if (!isShow) {
      setShouldShowFileTypeError(false);
    }
  }, [isShow]);

  const showMFRUploadDialog = ({ onPressSave, onPressCancel }) => {
    setIsShow(true);
    setTimeout(() => {
      setOnSave([onPressSave]);
      setOnCancel([onPressCancel]);
    }, 500);
  };

  const hideMFRUploadDialog = () => {
    setIsShow(false);
    setLoading(false);
    setBase64(null);
    setFile(null);
    setFilename(null);
    setOnSave([]);
    setOnCancel([]);
  };

  const onClickUpload = (e) => {
    e.preventDefault();
    setLoading(false);
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const fileOnChange = async (e) => {
    if (e.target.files.length) {
      const fileExtension = e.target.files[0].name.split(".").pop();
      if (fileExtension !== "pdf") {
        setShouldShowFileTypeError(true);
        return;
      }

      setShouldShowFileTypeError(false);
      setLoading(true);
      const file = e.target.files[0];
      let b64 = await toBase64(file);
      b64 = b64.split(",")[1];

      setFilename(file.name);
      setFile(file);
      setBase64(b64);
      setLoading(false);
    }
  };

  return (
    <MFRUploadDialogContext.Provider value={{ isShow, showMFRUploadDialog, hideMFRUploadDialog }}>
      {children}
      {isShow ? (
        <div className={styles.dialogContainer}>
          <div className={styles.boxContainer}>
            <div className={styles.header}>
              <span>MFR Upload</span>
              <button
                type="button"
                className={styles.mfrUploadClose}
                onClick={onCancel[0]}
                disabled={loading}
                data-dismiss="modal"
                aria-hidden="true"
              >
                &times;
              </button>
            </div>
            <div className={styles.body}>
              <input
                id="file-upload"
                type="file"
                name="file-upload"
                data-testid="file-upload"
                accept={MimeTypes.PDF}
                ref={fileUploadRef}
                style={{ display: "none" }}
                onChange={fileOnChange}
              />
              <small>Upload attachment.</small>
              <label
                htmlFor="file-upload"
                style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}
                onClick={onClickUpload}
              >
                <input
                  type="text"
                  placeholder="Add .pdf file..."
                  style={{ display: "flex", flex: 1 }}
                  readOnly
                  value={fileName || ""}
                />
                <a href="#" style={{ marginLeft: 10 }} onClick={(e) => e.preventDefault()}>
                  BROWSE FOR FILE
                </a>
              </label>
              <small className={styles.supportedFileFormat}>Supported file formats include: pdf.</small>
              {shouldShowFileTypeError && (
                <span className="error">
                  The file you are attempting to upload, is invalid. Please check the file type and try again.
                </span>
              )}
            </div>
            <div className={styles.footer}>
              <button
                className={[styles.button, styles.save].join(" ")}
                disabled={shouldDisableUploadBtn}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (shouldDisableUploadBtn) return false;
                  setLoading(true);
                  if (onSave[0]) onSave[0](e, base64, fileName, file);
                }}
              >
                {loading ? (
                  <img id="loading-spinner" src={loadingIcon} style={{ width: 20, height: 20 }} />
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </MFRUploadDialogContext.Provider>
  );
};

export default MFRUploadDialog;
